import React from 'react';
import { BarChart2, Calculator, Coins } from 'lucide-react';
import Button from '../../../../../components/ui/Button';
import { formatCurrency } from '../../../../../utils/formatting';

/**
 * Компонент боковой панели для быстрой настройки параметров вклада
 */
const QuickAdjustmentSidebar = ({ 
  formData,
  setFormData,
  errors,
  isCalculating,
  currencyOptions,
  capitalizationOptions
}) => {
  // Расчет текущей суммы вклада
  const depositAmount = parseFloat(formData.depositAmount) || 0;
  
  // Расчет текущего срока в месяцах
  const depositTermMonths = parseInt(formData.depositTerm, 10) || 0;
  
  // Получаем символ валюты
  const currency = currencyOptions.find(c => c.id === formData.currency);
  const currencySymbol = currency ? currency.symbol : '';
  
  // Обработчик изменения суммы вклада
  const handleDepositAmountChange = (e) => {
    const newValue = e.target.value;
    setFormData(prev => ({
      ...prev,
      depositAmount: newValue
    }));
  };
  
  // Обработчик изменения срока вклада
  const handleDepositTermChange = (e) => {
    const newValue = e.target.value;
    setFormData(prev => ({
      ...prev,
      depositTerm: newValue
    }));
  };
  
  // Обработчик изменения процентной ставки
  const handleInterestRateChange = (e) => {
    const newValue = e.target.value;
    setFormData(prev => ({
      ...prev,
      interestRate: newValue
    }));
  };
  
  // Предопределенные суммы вклада для быстрого выбора
  const predefinedAmounts = [
    { value: '50000', label: '50 тыс.' },
    { value: '300000', label: '300 тыс.' },
    { value: '700000', label: '700 тыс.' },
    { value: '1000000', label: '1 млн' }
  ];
  
  // Предопределенные сроки вклада для быстрого выбора
  const predefinedTerms = [
    { value: '3', label: '3 мес.' },
    { value: '6', label: '6 мес.' },
    { value: '12', label: '1 год' },
    { value: '24', label: '2 года' }
  ];
  
  // Функция расчета
  const calculateDeposit = () => {
    document.querySelector('button[type="submit"]')?.click();
  };
  
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <Calculator className="w-5 h-5 mr-2 text-green-500" />
        Быстрый подбор параметров
      </h3>
      
      <div className="space-y-5">
        {/* Сумма вклада */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label htmlFor="depositAmountSlider" className="text-gray-600 dark:text-gray-400">Сумма вклада</label>
            <span className="font-medium text-gray-900 dark:text-white">{formatCurrency(depositAmount, 0)} {currencySymbol}</span>
          </div>
          <input
            type="range"
            id="depositAmountSlider"
            min="10000"
            max="5000000"
            step="10000"
            value={depositAmount}
            onChange={handleDepositAmountChange}
            aria-label="Сумма вклада"
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-green-600"
          />
          
          {/* Кнопки с предопределенными суммами */}
          <div className="grid grid-cols-4 gap-1 mt-2">
            {predefinedAmounts.map(amount => (
              <button
                key={amount.value}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, depositAmount: amount.value }))}
                className={`px-2 py-1 text-xs rounded border transition-colors
                  ${formData.depositAmount === amount.value 
                    ? 'bg-green-100 border-green-300 text-green-700 dark:bg-green-900/30 dark:border-green-700 dark:text-green-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {amount.label}
              </button>
            ))}
          </div>
        </div>
        
        {/* Срок вклада */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label htmlFor="depositTermSlider" className="text-gray-600 dark:text-gray-400">Срок вклада</label>
            <span className="font-medium text-gray-900 dark:text-white">
              {depositTermMonths} {pluralize(depositTermMonths, 'месяц', 'месяца', 'месяцев')}
            </span>
          </div>
          <input
            type="range"
            id="depositTermSlider"
            min="1"
            max="60"
            step="1"
            value={depositTermMonths}
            onChange={handleDepositTermChange}
            aria-label="Срок вклада (месяцев)"
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-green-600"
          />
          
          {/* Кнопки с предопределенными сроками */}
          <div className="grid grid-cols-4 gap-1 mt-2">
            {predefinedTerms.map(term => (
              <button
                key={term.value}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, depositTerm: term.value }))}
                className={`px-2 py-1 text-xs rounded border transition-colors
                  ${formData.depositTerm === term.value 
                    ? 'bg-green-100 border-green-300 text-green-700 dark:bg-green-900/30 dark:border-green-700 dark:text-green-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {term.label}
              </button>
            ))}
          </div>
        </div>
        
        {/* Процентная ставка */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label htmlFor="interestRateSlider" className="text-gray-600 dark:text-gray-400">Процентная ставка</label>
            <span className="font-medium text-gray-900 dark:text-white">{formData.interestRate}%</span>
          </div>
          <input
            type="range"
            id="interestRateSlider"
            min="0.1"
            max="40"  // Изменено с 20 на 40
            step="0.1"
            value={formData.interestRate}
            onChange={handleInterestRateChange}
            aria-label="Процентная ставка (%)"
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-green-600"
            />
          
          {/* Предопределенные процентные ставки */}
          <div className="grid grid-cols-4 gap-1 mt-2">
            {['7.0', '12.0', '20.0', '30.0'].map(rate => (  // Изменены значения
                <button
                key={rate}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, interestRate: rate }))}
                className={`px-2 py-1 text-xs rounded border transition-colors
                    ${formData.interestRate === rate 
                    ? 'bg-green-100 border-green-300 text-green-700 dark:bg-green-900/30 dark:border-green-700 dark:text-green-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                    }`}
                >
                {rate}%
                </button>
            ))}
            </div>
        </div>
        
        {/* Выбор типа капитализации */}
        <div>
          <div className="flex justify-between items-center mb-2 text-sm">
            <label className="text-gray-600 dark:text-gray-400">Капитализация</label>
          </div>
          <div className="grid grid-cols-1 gap-2">
            {capitalizationOptions.map(option => (
              <button
                key={option.id}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, capitalization: option.id }))}
                className={`p-2 text-sm rounded-lg border transition-colors
                  ${formData.capitalization === option.id 
                    ? 'bg-green-100 border-green-300 text-green-700 dark:bg-green-900/30 dark:border-green-700 dark:text-green-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {option.name}
              </button>
            ))}
          </div>
        </div>
        
        {/* Кнопка пересчета */}
        <Button
          primary
          fullWidth
          onClick={calculateDeposit}
          icon={<BarChart2 className="w-4 h-4" />}
          disabled={isCalculating || Object.keys(errors).length > 0}
          isLoading={isCalculating}
        >
          Рассчитать доходность
        </Button>
        
        {/* Пример доходности */}
        <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg border border-green-100 dark:border-green-800">
          <div className="flex flex-col">
            <div className="flex justify-between mb-1">
              <span className="text-sm text-gray-600 dark:text-gray-400">Вклад:</span>
              <span className="text-sm font-medium text-gray-800 dark:text-gray-200">{formatCurrency(depositAmount, 0)} {currencySymbol}</span>
            </div>
            <div className="flex justify-between mb-1">
              <span className="text-sm text-gray-600 dark:text-gray-400">Срок:</span>
              <span className="text-sm font-medium text-gray-800 dark:text-gray-200">{depositTermMonths} мес.</span>
            </div>
            <div className="flex justify-between mb-1">
              <span className="text-sm text-gray-600 dark:text-gray-400">Доход:</span>
              <span className="text-sm font-medium text-green-600 dark:text-green-400">
                ~{formatCurrency(depositAmount * parseFloat(formData.interestRate) / 100 * depositTermMonths / 12, 0)} {currencySymbol}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-400">При капитализации:</span>
              <span className="text-sm font-medium text-green-600 dark:text-green-400">
                {formData.capitalization !== 'none' ? '≈+' + formatCurrency(depositAmount * 0.05, 0) + ' ' + currencySymbol : 'нет'}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Вспомогательная функция для склонения слов
const pluralize = (number, one, two, five) => {
  let n = Math.abs(number);
  n %= 100;
  if (n >= 5 && n <= 20) {
    return five;
  }
  n %= 10;
  if (n === 1) {
    return one;
  }
  if (n >= 2 && n <= 4) {
    return two;
  }
  return five;
};

export default QuickAdjustmentSidebar;